import React, { useState, Fragment, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import { Popover, Transition } from '@headlessui/react';
import {
  postComment,
  fetchComments,
  deleteComment,
  editComment,
} from '../../redux/actions/post';
import Avatar from '../Layout/Avatar';
import axios from 'axios';
import { server } from '../../server';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera, faVideo } from '@fortawesome/free-solid-svg-icons';
import LinkPreviewCard from '../Social/LinkPreviewCard';
import VideoPlayer from '../Content/VideoPlayer';
import { extractFirstUrl, normalizeLinkPreview } from '../../utils/linkPreview';
import { isShopProfile } from '../../utils/shopProfile';

const Comments = ({
  postId,
  totalComments,
  setTotalComments,
  comments = [],
  loggedInUserId,
  loggedInSellerId,
  commentProfileType,
  currentProfileId,
  onCancelCommenting,
}) => {
  const dispatch = useDispatch();
  const [commentText, setCommentText] = useState('');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [newCommentText, setNewCommentText] = useState('');
  const [currentCommentPage, setCommentCurrentPage] = useState(2);
  const [hasMoreComments, setHasMoreComments] = useState(
    comments.length < totalComments
  );
  const [newComments, setNewComments] = useState([]);
  const [likedComments, setLikedComments] = useState({});
  const [commentImages, setCommentImages] = useState([]);
  const [commentVideos, setCommentVideos] = useState([]);
  const [commentImagePreviews, setCommentImagePreviews] = useState([]);
  const [commentVideoPreviews, setCommentVideoPreviews] = useState([]);
  const [linkPreview, setLinkPreview] = useState(null);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const debounceTimer = useRef(null);
  const { user, seller } = useSelector((state) => ({
    user: state.user,
    seller: state.seller.seller,
  }));
  const normalizedCommentProfileType = isShopProfile(commentProfileType) ? 'Shop' : 'User';
  const commentDraftProfile = normalizedCommentProfileType === 'Shop' ? seller : user.user;
  const commentDraftAuthorName = commentDraftProfile?.name || 'You';
  const commentDraftDate = new Date().toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  useEffect(() => {
    if (comments.length > 0) {
      const initialLikedComments = {};
      comments.forEach((c) => {
        if (c._id && c.isLikedByCurrentUser) {
          initialLikedComments[c._id] = true;
        } else {
          initialLikedComments[c._id] = false;
        }
      });
      setLikedComments(initialLikedComments);
    }
  }, [comments]);

  useEffect(() => {
    if (commentImages.length > 0) {
      const newPreviews = commentImages.map((image) => URL.createObjectURL(image));
      setCommentImagePreviews(newPreviews);
      return () => newPreviews.forEach((url) => URL.revokeObjectURL(url));
    }

    setCommentImagePreviews([]);
  }, [commentImages]);

  useEffect(() => {
    if (commentVideos.length > 0) {
      const newPreviews = commentVideos.map((video) => URL.createObjectURL(video));
      setCommentVideoPreviews(newPreviews);
      return () => newPreviews.forEach((url) => URL.revokeObjectURL(url));
    }

    setCommentVideoPreviews([]);
  }, [commentVideos]);

  useEffect(() => {
    return () => clearTimeout(debounceTimer.current);
  }, []);

  // --- Like/unlike comment function ---
  const likeComment = async (commentId, likedBy, profileType, action) => {
    try {
      const response = await axios.patch(
        `${server}/like/comments/${commentId}/like?action=${action}`,
        { likedBy, profileType }
      );
      return response.data;
    } catch (error) {
      console.error(`Error ${action} comment:`, error);
      throw error;
    }
  };

  const handleLikeComment = async (commentId) => {
    const likedBy = currentProfileId;
    const profileType = normalizedCommentProfileType;
    const isAlreadyLiked = likedComments[commentId] || false;

    const action = isAlreadyLiked ? 'unlike' : 'like';

    // Optimistically update UI
    setLikedComments((prev) => ({
      ...prev,
      [commentId]: !isAlreadyLiked,
    }));

    try {
      await likeComment(commentId, likedBy, profileType, action);
    } catch (error) {
      // Revert UI change in case of an error
      setLikedComments((prev) => ({
        ...prev,
        [commentId]: isAlreadyLiked,
      }));
      toast.error(`Failed to ${action} comment`);
    }
  };

  // --- Fetch older comments (pagination) ---
  const loadMoreComments = useCallback(() => {
    if (!hasMoreComments) return;

    const nextPage = currentCommentPage;
    dispatch(fetchComments(postId, nextPage))
      .then((response) => {
        if (!response || !response.data || !response.data.comments) {
          throw new Error('Invalid response from server');
        }
        const moreComments = response.data.comments;
        if (moreComments.length === 0) {
          setHasMoreComments(false);
        } else {
          setNewComments((prev) => [...prev, ...moreComments]);
          setCommentCurrentPage((prev) => prev + 1);
        }
      })
      .catch((error) => {
        console.error('Error loading more comments:', error);
        toast.error('Failed to load more comments');
      });
  }, [currentCommentPage, dispatch, postId, hasMoreComments]);

  const fetchLinkPreview = async (url) => {
    try {
      const response = await axios.post(`${server}/posts/link-preview`, { url });
      setLinkPreview(normalizeLinkPreview(response.data));
    } catch (error) {
      console.error('Error fetching comment link preview:', error);
    }
  };

  const debounceFetchLinkPreview = (url) => {
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => fetchLinkPreview(url), 500);
  };

  const handleCommentTextChange = (event) => {
    const value = event.target.value;
    setCommentText(value);

    const url = extractFirstUrl(value);
    if (url) {
      debounceFetchLinkPreview(url);
    }
  };

  const handleImageSelection = (event) => {
    const files = Array.from(event.target.files || []);
    setCommentImages((prev) => [...prev, ...files]);
    event.target.value = '';
  };

  const handleVideoSelection = (event) => {
    const files = Array.from(event.target.files || []);
    setCommentVideos((prev) => [...prev, ...files]);
    event.target.value = '';
  };

  const resetCommentDraft = () => {
    setCommentText('');
    setCommentImages([]);
    setCommentVideos([]);
    setLinkPreview(null);
  };

  const handleCancelCommentDraft = (event) => {
    event.preventDefault();
    event.stopPropagation();
    resetCommentDraft();
    onCancelCommenting?.();
  };

  const renderCommentMedia = (comment) => {
    const images = Array.isArray(comment?.images) ? comment.images : [];
    const videos = Array.isArray(comment?.videos) ? comment.videos : [];

    if (!images.length && !videos.length && !comment?.link) {
      return null;
    }

    return (
      <div className="post-widget-comment-media-stack">
        {comment?.link && (
          <LinkPreviewCard preview={comment.link} className="post-widget-comment-link-preview" />
        )}
        {images.length > 0 && (
          <div className="post-widget-comment-image-grid">
            {images.map((image, index) => (
              <img
                key={image._id || image.url || index}
                src={image.url || image}
                alt="Comment attachment"
                className="post-widget-comment-image"
              />
            ))}
          </div>
        )}
        {videos.length > 0 && (
          <div className="post-widget-comment-video-grid">
            {videos.map((video, index) => {
              const videoUrl = video.key || video.url || video;
              return video.key ? (
                <div className="post-widget-comment-video" key={video._id || videoUrl || index}>
                  <VideoPlayer videoUrl={video.key} />
                </div>
              ) : (
                <video
                  key={video._id || videoUrl || index}
                  src={videoUrl}
                  controls
                  className="post-widget-comment-video"
                />
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // --- Post a new comment ---
  const handleCommentSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const submittedText = commentText.trim();
    const submittedLinkPreview = normalizeLinkPreview(linkPreview);
    const hasDraftContent = submittedText || commentImages.length > 0 || commentVideos.length > 0 || submittedLinkPreview;

    if (!hasDraftContent) {
      toast.error('Comment cannot be empty');
      return;
    }

    const formData = new FormData();
    formData.append('commentText', submittedText);
    formData.append('profileType', normalizedCommentProfileType);
    commentImages.forEach((image) => formData.append('images', image));
    commentVideos.forEach((video) => formData.append('videos', video));
    if (submittedLinkPreview) {
      formData.append('linkPreview', JSON.stringify(submittedLinkPreview));
    }

    const newComment = {
      _id: Math.random().toString(36).substr(2, 9),
      commentText: submittedText,
      profileType: normalizedCommentProfileType,
      images: commentImagePreviews.map((url) => ({ url })),
      videos: commentVideoPreviews.map((url) => ({ url })),
      link: submittedLinkPreview,
      userId:
        normalizedCommentProfileType === 'User'
          ? {
              _id: user.user?._id,
              name: user.user?.name,
              avatar: user.user?.avatar,
            }
          : null,
      shopId:
        normalizedCommentProfileType === 'Shop'
          ? {
              _id: seller?._id,
              name: seller?.name,
              avatar: seller?.avatar,
            }
          : null,
      createdAt: new Date().toISOString(),
      likeCount: 0,
      isLikedByCurrentUser: false,
    };

    // Optimistically add to local state
    setNewComments((prev) => [...prev, newComment]);
    resetCommentDraft();

    // Dispatch to server
    dispatch(postComment(postId, formData, normalizedCommentProfileType))
      .then((serverComment) => {
        // Merge server response with our local optimistic comment
        const updatedComment = {
          ...newComment,
          ...serverComment,

          // If server returned userId/shopId objects, override
          userId:
            serverComment.userId && typeof serverComment.userId === 'object'
              ? serverComment.userId
              : newComment.userId,
          shopId:
            serverComment.shopId && typeof serverComment.shopId === 'object'
              ? serverComment.shopId
              : newComment.shopId,
        };

        setNewComments((prev) =>
          prev.map((c) => (c._id === newComment._id ? updatedComment : c))
        );
        setTotalComments((prevCount) => prevCount + 1);
      })
      .catch(() => {
        // Remove the optimistic comment if request fails
        setNewComments((prev) => prev.filter((c) => c._id !== newComment._id));
        toast.error('Failed to post comment');
      });
  };

  // --- Delete a comment ---
  const handleDeleteComment = (commentId) => {
    dispatch(deleteComment(postId, commentId)).catch(() => {
      toast.error('Failed to delete comment');
    });
  };

  // --- Edit a comment ---
  const handleEditComment = (commentId, newText) => {
    dispatch(editComment(postId, commentId, newText)).catch(() => {
      toast.error('Failed to edit comment');
    });
    setEditingCommentId(null);
    setNewCommentText('');
  };

  const renderedComments = [...comments, ...newComments];

  // --- Rendering the list of comments ---
  return (
    <>
      {renderedComments.length > 0 && (
        <div className="post-widget-comments-list">
          {renderedComments.map((comment, index) => {

          // The server may return a field 'commenter' 
          // or it may return userId/shopId.
          // We'll check comment.commenter first:
          const directCommenter = comment?.commenter;
          
          // Or fall back to logic with shopId/userId:
          let profileType = comment?.profileType || '';
          if (!profileType) {
            profileType = comment?.shopId ? 'Shop' : 'User';
          }

          const fallbackCommenter =
            profileType === 'Shop' ? comment?.shopId : comment?.userId;

          // If directCommenter exists, use it, else fallback
          const commenter = directCommenter || fallbackCommenter;

          // Safely get name & avatar
          const commenterName = commenter?.name || 'Name';
          let commenterAvatarUrl = '/default-avatar.png';
          if (commenter) {
            // If it's a string
            if (typeof commenter.avatar === 'string') {
              commenterAvatarUrl = commenter.avatar;
            } else {
              // If it's an object: { url: "..." }
              commenterAvatarUrl = commenter.avatar?.url || '/default-avatar.png';
            }
          }

          return (
            <div
              key={comment?._id || `comment-${index}`}
              className="post-widget-comment-container"
            >
              <div className="post-widget-comment-avatar-container">
                <img
                  className="post-widget-comment-avatar"
                  src={commenterAvatarUrl}
                  alt={commenterName}
                />
              </div>

              <div className="post-widget-comment-content">
                <span className="post-widget-comment-author">
                  {commenterName}
                </span>
                <div className="post-widget-comment-box">
                  <div className="post-widget-comment-text-box">
                    {editingCommentId === comment?._id ? (
                      <input
                        type="text"
                        className="post-widget-comment-edit-input"
                        value={newCommentText}
                        onChange={(e) => setNewCommentText(e.target.value)}
                      />
                    ) : (
                      <span className="post-widget-comment-text">
                        {comment?.commentText}
                      </span>
                    )}
                    {renderCommentMedia(comment)}
                  </div>

                  {/* Like Button */}
                  <button
                    onClick={() => handleLikeComment(comment?._id)}
                    className="like-button"
                  >
                    {likedComments[comment?._id] ? (
                      <img
                        src="https://res.cloudinary.com/dzlopmfj8/image/upload/v1724845531/icon-BuzzBolt-02_ernlpk.png"
                        alt="Liked"
                        className="post-widget-like-icon"
                      />
                    ) : (
                      <img
                        src="https://res.cloudinary.com/dzlopmfj8/image/upload/v1724845557/icon-BuzzBolt-01_qhtx6n.png"
                        alt="Not Liked"
                        className="post-widget-like-icon"
                      />
                    )}
                    <span className="like-count">
                      {comment.likeCount || 0}
                    </span>
                  </button>
                </div>

                {/* Edit/Delete Options for Owner of this comment */}
                {commenter &&
                  ((comment?.profileType === 'User' &&
                    loggedInUserId === commenter._id) ||
                    (comment?.profileType === 'Shop' &&
                      loggedInSellerId === commenter._id)) && (
                    <Popover className="post-widget-comment-popover">
                      <Popover.Button>
                        <EllipsisHorizontalIcon
                          className="post-widget-comment-options-icon"
                          aria-hidden="true"
                        />
                      </Popover.Button>
                      <Transition
                        as={Fragment}
                        enter="post-widget-transition-enter"
                        enterFrom="post-widget-transition-enter-from"
                        enterTo="post-widget-transition-enter-to"
                        leave="post-widget-transition-leave"
                        leaveFrom="post-widget-transition-leave-from"
                        leaveTo="post-widget-transition-leave-to"
                      >
                        <Popover.Panel className="post-widget-comment-options-panel">
                          <div className="post-widget-comment-options-container">
                            <button
                              onClick={() => {
                                setEditingCommentId(comment?._id);
                                setNewCommentText(comment?.commentText);
                              }}
                              className="post-widget-comment-option-button"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteComment(comment?._id)}
                              className="post-widget-comment-option-button"
                            >
                              Delete
                            </button>
                          </div>
                        </Popover.Panel>
                      </Transition>
                    </Popover>
                  )}

                {/* Save button for editing */}
                {editingCommentId === comment?._id && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleEditComment(editingCommentId, newCommentText);
                    }}
                    className="post-widget-comment-save-button"
                  >
                    Save
                  </button>
                )}
              </div>
            </div>
          );
          })}
        </div>
      )}

      {/* Load more button if pagination is available */}
      {hasMoreComments && (
        <button onClick={loadMoreComments} className="load-more-comments-button">
          Load More Comments
        </button>
      )}

      {/* Form for new comment */}
      <div className="post-widget-comment-input-container">
        <div className="post-widget-comment-input-flex">
          <div className="post-widget-comment-draft-author">
            <Avatar />
            <div className="post-widget-comment-draft-meta">
              <span className="post-widget-comment-draft-name">{commentDraftAuthorName}</span>
              <span className="post-widget-comment-draft-date">{commentDraftDate}</span>
            </div>
          </div>
          <div className="post-widget-comment-input-flex-1">
            <form
              className="post-widget-comment-input-form"
              onSubmit={handleCommentSubmit}
            >
              <textarea
                value={commentText}
                onChange={handleCommentTextChange}
                rows={3}
                className="post-widget-comment-input"
                placeholder="Add your comment..."
              />
              {linkPreview && (
                <LinkPreviewCard
                  preview={linkPreview}
                  className="post-widget-comment-link-preview-draft"
                  onRemove={() => setLinkPreview(null)}
                />
              )}
              {(commentImagePreviews.length > 0 || commentVideoPreviews.length > 0) && (
                <div className="post-widget-comment-draft-media">
                  {commentImagePreviews.map((url, index) => (
                    <div className="post-widget-comment-draft-media-item" key={url}>
                      <img src={url} alt="Selected comment attachment" />
                      <button
                        type="button"
                        aria-label="Remove image attachment"
                        onClick={() => setCommentImages((prev) => prev.filter((_, itemIndex) => itemIndex !== index))}
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                  {commentVideoPreviews.map((url, index) => (
                    <div className="post-widget-comment-draft-media-item" key={url}>
                      <video src={url} muted />
                      <button
                        type="button"
                        aria-label="Remove video attachment"
                        onClick={() => setCommentVideos((prev) => prev.filter((_, itemIndex) => itemIndex !== index))}
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="post-widget-comment-submit-container">
                <div className="post-widget-comment-media-actions">
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="post-widget-comment-file-input"
                    onChange={handleImageSelection}
                  />
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/*"
                    multiple
                    className="post-widget-comment-file-input"
                    onChange={handleVideoSelection}
                  />
                  <button
                    type="button"
                    className="post-widget-comment-media-button"
                    aria-label="Add image"
                    onClick={() => imageInputRef.current?.click()}
                  >
                    <FontAwesomeIcon icon={faCamera} />
                  </button>
                  <button
                    type="button"
                    className="post-widget-comment-media-button"
                    aria-label="Add video"
                    onClick={() => videoInputRef.current?.click()}
                  >
                    <FontAwesomeIcon icon={faVideo} />
                  </button>
                </div>
                <div className="post-widget-comment-form-actions">
                  <button
                    type="button"
                    className="post-widget-comment-cancel-button"
                    onClick={handleCancelCommentDraft}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="post-widget-comment-submit-button">
                    POST
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default Comments;



